import { useMemo, useState } from 'react';
import { MapView, type BaseMap, type MapMarker } from '../components/MapView';
import { DevicePanel } from '../components/DevicePanel';
import { ClaimDevice } from '../components/ClaimDevice';
import { IconCrosshair, IconLayers, IconSearch } from '../components/Icons';
import { useToast } from '../components/Toast';
import type { Device } from '../api/types';
import { formatAgo, useT } from '../i18n';

interface Props {
  devices: Device[];
  loading: boolean;
  error: string | null;
  connected: boolean;
  onRetry: () => void;
  /** Customers can attach their own vehicles; admins manage them in Admin. */
  canClaim: boolean;
}

type Filter = 'all' | 'moving' | 'parked' | 'offline';

function tone(device: Device): 'moving' | 'parked' | 'offline' {
  if (!device.online) return 'offline';
  return (device.position?.speedKph ?? 0) > 4 ? 'moving' : 'parked';
}

export function Live({ devices, loading, error, connected, onRetry, canClaim }: Props) {
  const { t, lang } = useT();
  const toast = useToast();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [basemap, setBasemap] = useState<BaseMap>('clean');
  const [follow, setFollow] = useState(false);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(
    null,
  );
  const [fitKey, setFitKey] = useState(0);

  const counts = useMemo(() => {
    const result = { all: devices.length, moving: 0, parked: 0, offline: 0 };
    for (const device of devices) result[tone(device)] += 1;
    return result;
  }, [devices]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return devices.filter((device) => {
      if (filter !== 'all' && tone(device) !== filter) return false;
      if (!needle) return true;
      return [device.name, device.plate, device.imei, device.model]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle));
    });
  }, [devices, search, filter]);

  const markers: MapMarker[] = useMemo(
    () =>
      visible
        .filter((device) => device.position)
        .map((device) => ({
          id: device.id,
          latitude: device.position!.latitude,
          longitude: device.position!.longitude,
          course: device.position!.course,
          label: device.name,
          tone: tone(device),
          selected: device.id === selectedId,
        })),
    [visible, selectedId],
  );

  const selected = devices.find((device) => device.id === selectedId) ?? null;

  const locateMe = () => {
    if (!navigator.geolocation) {
      toast('err', t('error'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setFitKey((key) => key + 1);
      },
      // Denied or unavailable: say so rather than leaving the button dead.
      () => toast('err', t('error')),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const select = (id: number) => {
    setSelectedId(id);
    setFitKey((key) => key + 1);
  };

  if (loading) {
    return (
      <div className="live-layout">
        <section className="device-list">
          <div className="list-header">
            <h2>{t('vehicles')}</h2>
          </div>
          <div style={{ padding: '0 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton" style={{ height: 52 }} />
            ))}
          </div>
        </section>
        <div className="center-note">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="center-note">
        <p>{error}</p>
        <button type="button" className="btn primary" onClick={onRetry}>
          {t('retry')}
        </button>
      </div>
    );
  }

  const filters: Array<{ key: Filter; label: string }> = [
    { key: 'all', label: t('filterAll') },
    { key: 'moving', label: t('filterMoving') },
    { key: 'parked', label: t('filterParked') },
    { key: 'offline', label: t('filterOffline') },
  ];

  return (
    <div className="live-layout page">
      <section className="device-list">
        <div className="list-header">
          <h2>{t('vehicles')}</h2>
          <span className="count">{devices.length}</span>
          <span className={connected ? 'live-badge' : 'live-badge dead'}>
            <i />
            {connected ? 'LIVE' : t('offline')}
          </span>
        </div>

        <div className="search-wrap">
          <IconSearch />
          <input
            type="search"
            placeholder={t('search')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="filter-row">
          {filters.map((item) => (
            <button
              key={item.key}
              type="button"
              className={filter === item.key ? 'chip active' : 'chip'}
              onClick={() => setFilter(item.key)}
            >
              {item.label} {counts[item.key] > 0 && counts[item.key]}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <div className="empty-state">
            {devices.length === 0 ? (
              <>
                <p className="muted small">{t('noVehiclesYet')}</p>
                {canClaim && <p className="muted tiny">{t('noVehiclesHelp')}</p>}
              </>
            ) : (
              <p className="muted small">{t('noMatch')}</p>
            )}
          </div>
        ) : (
          <ul className="device-scroll">
            {visible.map((device) => (
              <li key={device.id}>
                <button
                  type="button"
                  className={device.id === selectedId ? 'device-row selected' : 'device-row'}
                  onClick={() => select(device.id)}
                >
                  <span className={`status-dot ${tone(device)}`} />
                  <span className="device-row-main">
                    <strong>{device.name}</strong>
                    <span className="device-row-meta">
                      {device.position ? (
                        <>
                          <span className="speed-tag">
                            {Math.round(device.position.speedKph)} km/h
                          </span>
                          <span>·</span>
                          <span>{formatAgo(device.lastSeenAt, lang)}</span>
                        </>
                      ) : (
                        <span>{t('noPosition')}</span>
                      )}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {canClaim && (
          <div className="list-footer">
            <ClaimDevice onClaimed={onRetry} />
          </div>
        )}
      </section>

      <div style={{ position: 'relative', minWidth: 0 }}>
        <MapView
          markers={markers}
          userLocation={userLocation}
          onMarkerClick={(id) => select(Number(id))}
          fitKey={fitKey}
          followId={follow ? selectedId : null}
          basemap={basemap}
        />

        <div className="map-controls">
          <div className="map-control-group">
            <button
              type="button"
              className={basemap === 'clean' ? 'map-btn active' : 'map-btn'}
              onClick={() => setBasemap('clean')}
            >
              <IconLayers />
              {t('mapClean')}
            </button>
            <button
              type="button"
              className={basemap === 'streets' ? 'map-btn active' : 'map-btn'}
              onClick={() => setBasemap('streets')}
            >
              {t('mapStreets')}
            </button>
            <button
              type="button"
              className={basemap === 'satellite' ? 'map-btn active' : 'map-btn'}
              onClick={() => setBasemap('satellite')}
            >
              {t('mapSatellite')}
            </button>
            <button
              type="button"
              className={basemap === 'night' ? 'map-btn active' : 'map-btn'}
              onClick={() => setBasemap('night')}
            >
              {t('mapNight')}
            </button>
          </div>

          <div className="map-control-group">
            <button
              type="button"
              className={follow ? 'map-btn active' : 'map-btn'}
              onClick={() => setFollow((value) => !value)}
              disabled={selectedId === null}
              title={selectedId === null ? t('noVehicles') : undefined}
            >
              <IconCrosshair />
              {t('follow')}
            </button>
            <button type="button" className="map-btn" onClick={() => setFitKey((k) => k + 1)}>
              {t('fitAll')}
            </button>
          </div>
        </div>

        {selected && (
          <DevicePanel
            device={selected}
            userLocation={userLocation}
            onLocateMe={locateMe}
            onClose={() => {
              setSelectedId(null);
              setFollow(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
